import React, { useState, useEffect } from 'react';
import { GaijinAuthService } from './auth/GaijinAuthService';
import './LoginPage.css';

interface LoginPageProps {
    onLoginSuccess: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const errorParam = urlParams.get('error');

        if (errorParam) {
            setError(errorParam === 'insufficient_permissions'
                ? 'Your Gaijin account does not have permission to access GameLift Streams.'
                : `Login failed: ${errorParam}`);
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
        }

        if (code) {
            setIsLoading(true);
            handleOAuthCallback();
        }
    }, []);

    const handleOAuthCallback = async () => {
        try {
            const success = await GaijinAuthService.handleCallback();
            if (success) {
                const postLoginQuery = sessionStorage.getItem('post_login_query') ?? '';
                sessionStorage.removeItem('post_login_query');
                window.history.replaceState({}, document.title, window.location.pathname + postLoginQuery);
                onLoginSuccess();
            } else {
                setError('Failed to complete login. Please try again.');
                setIsLoading(false);
            }
        } catch (error) {
            setError('Login failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
            setIsLoading(false);
        }
    };

    const handleOAuthLogin = async () => {
        try {
            setError(null);
            setIsLoading(true);
            await GaijinAuthService.initiateLogin();
        } catch (error) {
            setError('Failed to initiate login: ' + (error instanceof Error ? error.message : 'Unknown error'));
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="login-page">
                <div className="login-kicker">Authorizing</div>
                <h1 className="login-title">Signing in…</h1>
                <div className="login-sweep"></div>
                <p className="login-log">
                    exchanging authorization code → access token
                    <br />→ SSO JWT · checking access
                </p>
            </div>
        );
    }

    return (
        <div className="login-page">
            <div className="login-kicker">Gaijin SSO</div>
            <h1 className="login-title">GameLift Streams</h1>
            <div className="login-rule"></div>

            {error
                ? <div className="login-notice">
                    <div className="login-notice__label">Access denied</div>
                    <div className="login-notice__text">{error}</div>
                </div>
                : <p className="login-body">Sign in with your Gaijin account.</p>
            }

            <button
                className="gls-btn gls-btn--primary"
                onClick={handleOAuthLogin}
                disabled={isLoading}
            >
                {error ? 'Try another account' : 'Sign in with Gaijin account'}
            </button>
        </div>
    );
};

export default LoginPage;
